/* Verbatim port of actionPlan from service/review/index.html (~1222-1270).
 *
 * PARAMETERIZED (rule a): reads the global `STATE` (directly, and
 * transitively via entityHeadline/threatItems) — takes `state:
 * AppPayloadState` as an explicit first parameter, same as every other
 * derivation.
 *
 * PARAMETERIZED (rule a, second instance): also reads the module-level
 * mutable `ACT_STATUS` (~1272 `let ACT_STATUS = {}`, populated from
 * localStorage by loadActStatus() — a DOM/localStorage-touching function
 * correctly excluded from this extraction as F2 material per the brief's
 * impurity rule). ACT_STATUS is exactly STATE-shaped for this purpose — a
 * plain lookup object read at derivation time — so it gets the same
 * treatment: an explicit `actStatus` parameter, defaulted to `{}` to match
 * ACT_STATUS's pre-load value (and loadActStatus()'s own catch-fallback).
 *
 * `assetForUrl` moved to derive/exec.ts — see the DEVIATION note there.
 *
 * The conflicts-loop callback's unused `i` index parameter (present in the
 * original too) is renamed `_i` — same noUnusedParameters accommodation as
 * derive/axes.ts's `_asset`, not a behavior change.
 */
import { slugify } from '../lib/format.ts';
import { threatItems } from './exec.ts';
import { entityHeadline } from './entity.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { OpportunityAction, Priority } from '../types/contract.ts';

/** The human title for a research opportunity — e.g. "Claim — TripAdvisor" —
 * from its action verb + platform. The Opportunity contract carries NO `title`
 * field (it's {platform, action, rationale, …}), so the row renderers that bound
 * `o.title` fell through to the literal word "Opportunity" on 5 screens
 * (design-fidelity P0-1). This is the single shared source of the title so every
 * surface (overview "What needs improving", the Search/AI "Findings &
 * opportunities" strips, the derived board task) reads identically. */
const OPP_VERB: Record<OpportunityAction, string> = {
  claim: 'Claim', create: 'Create', reactivate: 'Reactivate', consolidate: 'Consolidate',
};
export function opportunityTitle(o: { action: OpportunityAction; platform: string }): string {
  return `${OPP_VERB[o.action] || 'Address'} — ${o.platform || 'this platform'}`;
}

/** One row of the §7 action board (and the §0 top-five list). `id` is the
 * stable row key — slugify(what).slice(0,40) — that actStatusStore and the
 * shared WS board key statuses on; `status` is 'open' until the AM moves it. */
export interface ActionRow {
  id: string;
  status: string;
  what: string;
  detail: string;
  category: string;
  priority: Priority;
  effort: 'Low' | 'Medium' | 'High';
  source: string;
}

/* ── THE ACTION PLAN (canonical; §0 top-5 and §7 table both read this) ── */
export function actionPlan(state: AppPayloadState, actStatus: Record<string, string> = {}): ActionRow[] {
  const rows: ActionRow[] = []; const seenKeys = new Set<string>();
  const add = (r: Omit<ActionRow, 'id' | 'status'>) => {
    const key = slugify(r.what).slice(0,40);
    if (seenKeys.has(key)) return; seenKeys.add(key);
    rows.push(Object.assign({ id:key, status: actStatus[key] || 'open' }, r));
  };
  const eh0 = entityHeadline(state);
  // 1. research-derived opportunities (claim / create / fix); Wikidata-flavored
  // ones are skipped when the aggregated Wikidata worksheet row (below) covers them
  (state.opportunities||[]).forEach((o) => {
    if (eh0 && eh0.fix > 0 && /wikidata/i.test(o.platform || '')) return;
    add({
    what: opportunityTitle(o),
    detail: o.rationale, category:'Profiles & listings', priority: o.priority || 'medium',
    effort: o.action === 'claim' ? 'Low' : o.action === 'create' ? 'Medium' : 'Low', source:'§6 Gaps' }); });
  // 2. factual conflicts → resolve actions
  (state.conflicts||[]).forEach((c,_i) => {
    const head = c.description.split(':')[0] ?? '';
    add({ what: `Resolve — ${head}`, detail: c.description, category:'Consistency',
          priority: /official website|CEO|leadership/i.test(c.description) ? 'high' : 'medium',
          effort:'Low', source:'§6 Conflicts' });
  });
  // 3. Wikidata worksheet (aggregate)
  const eh = entityHeadline(state);
  if (eh && eh.fix > 0) add({
    what: `Update Wikidata — ${eh.fix} claim${eh.fix>1?'s':''} to fix or add`,
    detail: 'The worksheet in §2 lists each claim with its source; the QuickStatements export is ready to paste.',
    category:'Entity & Knowledge Panel', priority:'high', effort:'Low', source:'§2 worksheet' });
  // 4. missing cross-links (aggregate, from the measured link crawl)
  if (eh && eh.measured && eh.bridges > 0) add({
    what: `Build ${eh.bridges} missing cross-links (sameAs · Wikidata · profile bios)`,
    detail: `The link crawl ranked every missing connection in §2${eh.orphans ? `, including ${eh.orphans} client properties nothing points at` : ''}. The sameAs export closes the site-side gaps in one snippet; the rest are Wikidata claims and profile website fields.`,
    category:'Entity & Knowledge Panel', priority:'high', effort:'Low', source:'§2 cross-linking' });
  else if (eh && eh.weak > 0) add({
    what: `Add ${eh.weak} official profiles to the site’s sameAs markup + Wikidata`,
    detail: 'Nothing authoritative currently confirms these profiles belong to the client — a weak entity signal. The sameAs export in §2 is ready for the web team.',
    category:'Entity & Knowledge Panel', priority:'medium', effort:'Low', source:'§2 cross-linking' });
  // 5. top threats (search defense + AI narrative)
  threatItems(state).filter((t) => t.sev !== 'low').slice(0, 6).forEach((t) => add({
    what: `${t.kind === 'search' ? 'Counter on search' : 'Counter in AI narrative'} — ${t.domain}`,
    detail: `${t.why} ${t.action}`, category: t.kind === 'search' ? 'Search defense' : 'AI narrative',
    priority: t.sev === 'high' ? 'high' : 'medium',
    effort: /wikipedia/i.test(t.domain||'') ? 'High' : 'Medium', source: t.kind === 'search' ? '§1 Search' : '§3 AI narrative' }));
  const P: Record<Priority, number> = {high:0, medium:1, low:2};
  rows.sort((a,b) => (P[a.priority]-P[b.priority]) || a.category.localeCompare(b.category));
  return rows;
}
